import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, CartesianGrid } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import type { MonthlyData } from "@/hooks/admin/AdminDashboardTab/usePropertyYearlyMetrics";

interface PropertyIncomeExpensesChartProps {
  incomeData?: MonthlyData[];
  expensesData?: MonthlyData[];
  isLoading?: boolean;
  year: number;
}

const chartConfig = {
  income: {
    label: "Ingresos",
    color: "hsl(var(--success))",
  },
  expenses: {
    label: "Gastos",
    color: "hsl(var(--destructive))",
  },
} satisfies ChartConfig;

const PropertyIncomeExpensesChart = ({
  incomeData = [],
  expensesData = [],
  isLoading = false,
  year,
}: PropertyIncomeExpensesChartProps) => {
  // Combine data for the chart
  const chartData = incomeData.map((income, index) => ({
    month: income.month.substring(0, 3), // Shorten month name
    fullMonth: income.month,
    income: income.amount,
    expenses: expensesData[index]?.amount ?? 0,
  }));

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ingresos y Gastos {year}</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Ingresos y Gastos {year}</CardTitle>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="h-[300px] w-full">
          <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
            <XAxis 
              dataKey="month" 
              tickLine={false} 
              axisLine={false}
              tick={{ fontSize: 12 }}
              className="fill-muted-foreground"
            />
            <YAxis 
              tickLine={false} 
              axisLine={false}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
              tick={{ fontSize: 12 }}
              width={50}
              className="fill-muted-foreground"
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  formatter={(value, name) => {
                    const formattedValue = new Intl.NumberFormat("es-MX", {
                      style: "currency",
                      currency: "MXN",
                      minimumFractionDigits: 0,
                    }).format(Number(value));
                    return (
                      <span>
                        {name === "income" ? "Ingresos" : "Gastos"}: {formattedValue}
                      </span>
                    );
                  }}
                  labelFormatter={(_, payload) => {
                    if (payload && payload[0]) {
                      return payload[0].payload.fullMonth;
                    }
                    return "";
                  }}
                />
              }
            />
            <Bar 
              dataKey="income" 
              fill="var(--color-income)" 
              radius={[4, 4, 0, 0]}
            />
            <Bar 
              dataKey="expenses" 
              fill="var(--color-expenses)" 
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ChartContainer>
        
        {/* Legend */}
        <div className="flex justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-success" />
            <span className="text-muted-foreground">Ingresos</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-sm bg-destructive" />
            <span className="text-muted-foreground">Gastos</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default PropertyIncomeExpensesChart;
